import { CancelOutlined } from '@mui/icons-material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Badge,
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { visuallyHidden } from '@mui/utils';
import React, { useEffect, useState } from 'react';

import { formatMoney } from '../../functions/utils';
import CustomButton from '../Button';
import DataExporter from '../DataExporter';
import { cellFormat } from '../Table/cellFormat';
import { LightTooltip } from '../WhiteTooltip';

type RowData = {
  [key: string]: any;
};

type Action = {
  action: (number: any) => void;
  actionLabel: string;
  canShow?: any;
  notifications?: boolean;
};

export type Header = {
  label: string;
  key: string;
  type?: string;
  canNull?: boolean;
  handleChange?: any;
  handleAction?: any;
  icon?: any;
  onClick?: any;
  keyDisabled?: string;
  customComponent?: any;
  description?: string;
  sortKey?: string;
  formatter?: (value: Date, format?: string) => string;
};

interface EnhancedTableProps {
  data: RowData[];
  setData: any;
  actions?: Action[];
  caption?: string;
  dataHeaders: Header[];
  exportHeaders?: Header[];
  tableSize?: 'small' | 'medium';
  showActions?: boolean;
  finalSumRow?: boolean;
  filters?: {
    key: string;
    label: string;
    options?: string[];
    startDate?: any;
    endDate?: any;
    setStartDate?: any;
    setEndDate?: any;
    type?: string;
  }[];
  loading?: boolean;
  rowsPerPageDefault?: number;
  openFilters?: any;
  setOpenFilters?: any;
  selected?: any;
  setSelected?: any;
  cantSelectRow?: any;
  updateValues?: any;
  completeTable?: boolean;
  handleClickRow?: any;
  canSelectAll?: boolean;
  filterOptions?: any;
  buttons?: any;
  filterButton?: boolean;
  exportToFile?: 'csv' | 'xlsx';
  searchData?: any;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  setPagination: any;
  lastElement?: boolean;
  exportComplete?: boolean;
  setLoading?: any;
  headerBackgroundColor?: string;
  showInfoButton?: boolean;
  onInfoClick?: (rowId: number | string, rowData: any) => void;
  showFirstLastPageControls?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      width: '100%', // Asegura que la tabla se ajuste a su contenedor
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
      maxWidth: '100%',
      overflowX: 'auto', // Permite el desplazamiento horizontal si es necesario
      display: 'block', // Asegura que el contenedor maneje correctamente el overflow
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
  }),
);
type Order = 'asc' | 'desc';

function descendingComparator<T>(
  a: T,
  b: T,
  orderBy: keyof T,
  sortKey?: string,
) {
  let valueA: any, valueB: any;

  if (sortKey) {
    const keys = sortKey.split('.');
    valueA = keys.reduce((obj: any, key) => obj?.[key], a);
    valueB = keys.reduce((obj: any, key) => obj?.[key], b);
  } else {
    valueA = a[orderBy];
    valueB = b[orderBy];
  }

  if (valueA === null && valueB !== null) {
    return 1;
  }

  // Check if valueB is null and valueA is not
  if (valueB === null && valueA !== null) {
    return -1; // a is greater than b
  }

  // If both are null or both are non-null, perform the usual comparison
  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }

  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
  sortKey?: string,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, sortKey)
    : (a, b) => -descendingComparator(a, b, orderBy, sortKey);
}

function parseArrayToNumberSort<T>(
  array: T[],
  orderBy: keyof T,
  sortKey?: string,
): T[] {
  const parsedArray = array.map((element) => {
    let value: any;
    if (sortKey) {
      const keys = sortKey.split('.');
      value = keys.reduce((obj: any, key) => obj?.[key], element);
    } else {
      value = element[orderBy];
    }

    if (typeof value === 'string' && value !== '' && !isNaN(Number(value))) {
      return { ...element, [orderBy]: Number(value) };
    }
    return element;
  });

  // Verificar si todos los valores son números después del parsing
  const allNumbers = parsedArray.every((el) => {
    let value: any;
    if (sortKey) {
      const keys = sortKey.split('.');
      value = keys.reduce((obj: any, key) => obj?.[key], el);
    } else {
      value = el[orderBy];
    }
    return typeof value === 'number';
  });

  if (allNumbers) {
    return parsedArray;
  }
  return array;
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
  orderBy: string,
  sortKey?: string,
) {
  const parsedArray = parseArrayToNumberSort(
    [...array],
    orderBy as keyof T,
    sortKey,
  );
  const stabilizedThis = parsedArray.map(
    (el, index) => [el, index] as [T, number],
  );
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface EnhancedTableHead {
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  headCells: any;
  showActions: any;
  showInfoButton?: boolean;
  selected?: any;
  handleSelectAllClick?: any;
  selectedAll?: any;
  setSelectedAll?: any;
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

const TablePaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}: TablePaginationActionsProps) => {
  const theme = useTheme();

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
    </Box>
  );
};

function EnhancedTableHead(props: EnhancedTableHead) {
  const {
    headCells,
    order,
    orderBy,
    rowCount,
    onRequestSort,
    showActions,
    showInfoButton,
    selected,
    handleSelectAllClick,
    selectedAll,
    setSelectedAll,
  } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead style={{ background: '#efefef' }}>
      <TableRow>
        {selected && !handleSelectAllClick && (
          <TableCell align="right" style={{ width: '20px' }}></TableCell>
        )}
        {selected && handleSelectAllClick && (
          <TableCell align="left" style={{ width: '20px' }}>
            <Checkbox
              style={{ padding: 0, marginLeft: '-3px' }}
              color="primary"
              checked={selectedAll}
              onClick={(e) => handleSelectAllClick(e)}
            />
          </TableCell>
        )}
        {showInfoButton && (
          <TableCell align="center" style={{ width: '50px' }}></TableCell>
        )}
        {headCells.map((headCell: any, index: number) => (
          <TableCell
            key={headCell.key}
            align={headCell.numeric ? 'right' : 'left'}
            style={{ padding: '8px 12px' }}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.key}
              direction={orderBy === headCell.key ? order : 'asc'}
              onClick={createSortHandler(headCell.key)}
            >
              {headCell.description ? (
                <LightTooltip
                  title={
                    <Typography variant="body1" padding={1}>
                      {headCell.description}
                    </Typography>
                  }
                  placement="top"
                >
                  {headCell.label}
                </LightTooltip>
              ) : (
                headCell.label
              )}
              {orderBy === headCell.key ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        {showActions ? <TableCell align="right"></TableCell> : null}
      </TableRow>
    </TableHead>
  );
}

const EnhacedTableBody = (props: any) => {
  const {
    visibleRows,
    dataHeaders,
    selected,
    cantSelectRow,
    handleClickRow,
    actions,
    finalSumRow,
    handleClick,
    anchorEls,
    openMenus,
    completeTable,
    pagination,
    tableSize,
    openTooltipIndex,
    setOpenTooltipIndex,
    classes,
    firstCellStyle,
    data,
    setOpenMenus,
    setAnchorEls,
    setSelected,
    updateValues,
    showInfoButton,
    onInfoClick,
  } = props;

  const handleClickSelected = (id: number, row: any) => {
    if (cantSelectRow?.(row)) return;

    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    if (updateValues) {
      updateValues(newSelected);
    }
    setSelected(newSelected);
  };
  const isSelected = (id: number) => selected?.indexOf(id) !== -1;
  const [selectedRow, setSelectedRow] = useState<any>([]);

  const handleSelectRow = (id: number | string) => {
    if (selectedRow === id) {
      return setSelectedRow(null);
    }
    setSelectedRow(id);
  };

  const handleClose = (index: number) => {
    // Create a copy of the openMenus array and set the clicked menu to close
    const newOpenMenus = [...openMenus];
    newOpenMenus[index] = false;

    setOpenMenus(newOpenMenus);
    setAnchorEls(anchorEls.map(() => null)); // Reset all anchorEls to null
  };

  function calculateSums(
    data: RowData[],
    headers: Header[],
  ): { [key: string]: any } {
    const sums: { [key: string]: any } = {};

    headers.forEach((header, index) => {
      sums[header.key] = data.reduce((acc, row) => {
        if (typeof row[header.key] === 'number') {
          return acc + row[header.key];
        }
        return acc;
      }, 0);
    });

    return sums;
  }

  const sums = calculateSums(data, dataHeaders);

  return (
    <TableBody className={classes.tableBody}>
      {visibleRows().map((row: any, index: any) => {
        const isItemSelected = isSelected(row.id as number);
        const haveHandleChange = dataHeaders.some((x: any) => x.handleChange);
        return (
          <TableRow
            key={index}
            style={{
              ...(handleClickRow ? { cursor: 'pointer' } : {}),
              ...(selectedRow === row.id ? { backgroundColor: '#F5F5F5' } : {}),
            }}
            onClick={() => {
              if (selected && !haveHandleChange) {
                handleClickSelected(row.id as number, row);
              }
              if (handleClickRow) {
                handleClickRow(row.id);
                handleSelectRow(row.id);
              }
            }}
          >
            {selected && (
              <TableCell padding="checkbox" style={{ width: '20px' }}>
                {cantSelectRow?.(row) ? (
                  <CancelOutlined
                    color="disabled"
                    style={{ padding: '6px 10px' }}
                  />
                ) : (
                  <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClickSelected(row.id as number, row);
                    }}
                  />
                )}
              </TableCell>
            )}
            {dataHeaders.map((x: any, rowIndex: any) => {
              if (x.customComponent) {
                return (
                  <React.Fragment key={rowIndex}>
                    <TableCell
                      style={{
                        ...firstCellStyle,
                        ...(x.onClick ? { cursor: 'pointer' } : {}),
                      }}
                    >
                      {x.customComponent(row[x.key], row)}
                    </TableCell>
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={rowIndex}>
                  <Tooltip
                    title={!x.type ? row[x.key] : ''}
                    open={openTooltipIndex === `${index}-${rowIndex}`}
                    onClose={() => setOpenTooltipIndex(null)}
                    PopperProps={{
                      style: { maxWidth: '1000px', whiteSpace: 'normal' }, // This allows the tooltip to expand as needed
                    }}
                  >
                    <TableCell
                      style={{
                        ...firstCellStyle,
                        ...(x.onClick ? { cursor: 'pointer' } : {}),
                      }}
                      onMouseEnter={() => {
                        setOpenTooltipIndex(`${index}-${rowIndex}`);
                      }}
                      onMouseLeave={() => setOpenTooltipIndex(null)} // Close the tooltip when the mouse leaves
                      onClick={(e) => {
                        if (x.onClick) {
                          e.stopPropagation();
                          x.onClick(x.key, row.id);
                        }
                      }}
                    >
                      {x.handleAction && x.icon ? (
                        <IconButton
                          disabled={
                            x.keyDisabled
                              ? row[x.keyDisabled]
                                ? false
                                : true
                              : false
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            x.handleAction(row.id);
                          }}
                        >
                          {x.icon}
                        </IconButton>
                      ) : null}
                      {x.handleChange ? (
                        <TextField
                          value={
                            x.type === 'money'
                              ? formatMoney(row[x.key] as number)
                              : row[x.key]
                          }
                          id={String(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          name={String(row.id)}
                          onChange={(e) => x.handleChange(e)}
                        />
                      ) : (
                        cellFormat(
                          row[x.key],
                          row.rowType ? row.rowType : x.type,
                          x.canNull,
                          x.nullValue,
                          x.handleAction,
                          row.id,
                          row,
                          x.customAction,
                          x.extraData,
                          x.formatter,
                        )
                      )}
                    </TableCell>
                  </Tooltip>
                </React.Fragment>
              );
            })}
            {actions.length ? (
              <TableCell
                style={firstCellStyle}
                key={index}
                size={tableSize}
                align="right"
              >
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClick(index, event);
                  }}
                >
                  <Badge badgeContent={row.notifications} color="error">
                    <MoreVertIcon style={{ color: 'black' }} />
                  </Badge>
                </IconButton>
                <Menu
                  anchorEl={anchorEls[index]}
                  open={openMenus[index]}
                  onClose={() => handleClose(index)}
                  onClick={() => handleClose(index)}
                >
                  {actions.map((x: any, index: any) => {
                    if (x.canShow) {
                      if (x.canShow(row.id)) {
                        return (
                          <MenuItem
                            key={index}
                            onClick={() => {
                              x.action(row.id as number);
                            }}
                          >
                            <Badge
                              badgeContent={
                                x.notifications ? row.notifications : null
                              }
                              color="error"
                            >
                              {x.actionLabel}{' '}
                            </Badge>
                          </MenuItem>
                        );
                      }
                    } else {
                      return (
                        <MenuItem
                          key={index}
                          onClick={() => x.action(row.id as number)}
                        >
                          {x.actionLabel}{' '}
                        </MenuItem>
                      );
                    }
                  })}
                </Menu>
              </TableCell>
            ) : null}
          </TableRow>
        );
      })}
      {completeTable &&
        Array.from({
          length: pagination.pageSize - visibleRows().length,
        }).map((_, index) => (
          <TableRow key={`empty-${index}`}>
            {selected && <TableCell />}{' '}
            {/* Include this if you have a checkbox column */}
            {dataHeaders.map((_: any, cellIndex: any) => (
              <React.Fragment key={`empty-cell-${cellIndex}`}>
                <TableCell style={{ border: 0 }} />
                {showInfoButton && cellIndex === 0 && (
                  <TableCell style={{ border: 0 }} />
                )}
              </React.Fragment>
            ))}
            {actions.length ? <TableCell style={{ border: 0 }} /> : null}{' '}
            {/* Include this if you have an actions column */}
          </TableRow>
        ))}
      {finalSumRow && (
        <TableRow style={{ borderTop: '1.5px solid' }}>
          <TableCell size={tableSize}>
            <b>Total</b>
          </TableCell>
          {selected && <TableCell size={tableSize}>-</TableCell>}
          {dataHeaders.slice(1).map((header: any, index: any) => (
            <TableCell key={index} size={tableSize}>
              {typeof sums[header.key] === 'number' &&
              (header.type === 'money' || header.type === 'good-money')
                ? formatMoney(sums[header.key])
                : ''}
            </TableCell>
          ))}
          {actions.length ? <TableCell align="right"></TableCell> : null}{' '}
          {/* empty cell if there are actions */}
        </TableRow>
      )}
    </TableBody>
  );
};

const LoadingTableBody = (props: any) => {
  const { pagination, dataHeaders, showInfoButton, actions } = props;

  return (
    <TableBody>
      {[...Array(pagination.pageSize)].map((_, index) => (
        <TableRow key={`loading-${index}`}>
          {dataHeaders.map((_: any, cellIndex: any) => (
            <React.Fragment key={`loading-cell-${cellIndex}`}>
              <TableCell>
                <Skeleton animation="wave" variant="text" height={9} />
              </TableCell>
              {showInfoButton && cellIndex === 0 && (
                <TableCell key={`loading-info-${index}`}>
                  <Skeleton
                    animation="wave"
                    variant="circular"
                    width={24}
                    height={24}
                  />
                </TableCell>
              )}
            </React.Fragment>
          ))}
          {actions?.length ? (
            <TableCell key={`loading-actions-${index}`}>
              <Skeleton
                animation="wave"
                variant="circular"
                width={24}
                height={24}
              />
            </TableCell>
          ) : null}
        </TableRow>
      ))}
    </TableBody>
  );
};

const TableWithPagination: React.FC<EnhancedTableProps> = ({
  data,
  setData,
  caption,
  tableSize = 'medium',
  dataHeaders = [],
  exportHeaders,
  actions = [],
  finalSumRow = false,
  filters = [],
  rowsPerPageDefault = 5,
  loading,
  selected,
  setSelected,
  cantSelectRow,
  // setOpenFilters,
  updateValues,
  completeTable,
  handleClickRow,
  canSelectAll,
  buttons,
  exportToFile,
  searchData,
  pagination,
  setPagination,
  lastElement,
  exportComplete,
  setLoading,
  headerBackgroundColor = '#EFEFEF',
  showInfoButton = false,
  onInfoClick,
  showFirstLastPageControls = false,
  // filterOptions,
}) => {
  const [openMenus, setOpenMenus] = React.useState(data.map(() => false));
  const [anchorEls, setAnchorEls] = React.useState<(HTMLElement | null)[]>(
    data.map(() => null),
  );
  const [dataAvailable, setDataAvailable] = useState(data);
  const classes = useStyles();
  const [order, setOrder] = React.useState<any>('');
  const [orderBy, setOrderBy] = React.useState<any>('');
  const [openTooltipIndex, setOpenTooltipIndex] = useState<string | null>(null);
  const [selectedAll, setSelectedAll] = useState(false);

  const firstCellStyle: any = {
    maxWidth: '150px',
    overflow: 'hidden', // Ensure the content doesn't overflow.
    textOverflow: 'ellipsis', // If the text overflows, it will end with "..."
    whiteSpace: 'nowrap', // Ensure the text doesn't wrap to the next line.
    padding: '4px 12px',
    margin: '0px',
  };

  const handleClick = (
    index: number,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    // Create a copy of the openMenus array and set the clicked menu to open
    const newOpenMenus = [...openMenus];
    newOpenMenus[index] = true;

    // Use optional chaining to safely access event.currentTarget
    const anchorEl = event.currentTarget;

    setOpenMenus(newOpenMenus);
    setAnchorEls((prevAnchorEls: any) => {
      // Create a copy of the anchorEls array and set the anchorEl for the clicked menu
      const newAnchorEls: HTMLElement[] | null[] = [...prevAnchorEls];
      newAnchorEls[index] = anchorEl;
      return newAnchorEls;
    });
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    try {
      if (setLoading) setLoading(true);
      const newDataObject = await searchData({
        page: newPage + 1,
        pageSize: pagination.pageSize,
      });
      setPagination(newDataObject.pagination);
      setData(newDataObject.data);
      if (setLoading) setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    try {
      if (setLoading) setLoading(true);
      const newDataObject = await searchData({
        page: 1,
        pageSize: newPageSize,
      });
      setPagination(newDataObject.pagination);
      setData(newDataObject.data);
      if (setLoading) setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const visibleRowsCache = React.useMemo(() => {
    // Encontrar el header correspondiente para obtener la sortKey
    const currentHeader = dataHeaders.find((h) => h.key === orderBy);
    const sortKey = currentHeader?.sortKey;

    return stableSort(
      dataAvailable,
      getComparator(order, orderBy, sortKey),
      orderBy,
      sortKey,
    ).slice(
      pagination.page * pagination.pageSize,
      pagination.page * pagination.pageSize + pagination.pageSize,
    );
  }, [
    dataAvailable,
    order,
    orderBy,
    pagination.page,
    pagination.pageSize,
    dataHeaders,
  ]);

  const visibleRows = () => {
    // Encontrar el header correspondiente para obtener la sortKey
    const currentHeader = dataHeaders.find((h) => h.key === orderBy);
    const sortKey = currentHeader?.sortKey;

    return stableSort(
      dataAvailable,
      getComparator(order, orderBy, sortKey),
      orderBy,
      sortKey,
    );
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  useEffect(() => {
    setDataAvailable(data);
  }, [data]);

  const handleSelectAllClick = (event: any) => {
    if (event.target.checked) {
      const newSelected = visibleRows().map((n: any) => n.id);
      setSelected(newSelected);
      setSelectedAll(true);
      return;
    }
    setSelected([]);
    setSelectedAll(false);
  };

  return (
    <div style={{ padding: lastElement ? '0px 0px 100px 0px' : '0px' }}>
      <div
        style={{
          backgroundColor: headerBackgroundColor,
        }}
      >
        <div
          style={
            caption
              ? {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: filters.length ? '0px 0px' : '20px 0px',
                }
              : {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: '0px',
                }
          }
        >
          {caption && <Typography variant="body1">{caption}</Typography>}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingBottom: buttons?.length ? 20 : 0,
            }}
          >
            {buttons?.map((x: any, index: any) => (
              <>
                {x.customButton && <>{x.customButton}</>}
                {!x.customButton && (
                  <div key={index} style={{ paddingLeft: '10px' }}>
                    <CustomButton
                      variant="contained"
                      color={x.color}
                      onClick={x.action}
                    >
                      {x.name}
                    </CustomButton>
                  </div>
                )}
              </>
            ))}
          </div>
        </div>
        <TableContainer
          className={classes.tableContainer}
          style={{ width: '100%' }}
        >
          <Table className={classes.table} style={{ borderRadius: '10px' }}>
            <EnhancedTableHead
              headCells={dataHeaders}
              selected={selected}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={dataAvailable.length}
              showActions={actions.length}
              showInfoButton={showInfoButton}
              handleSelectAllClick={canSelectAll ? handleSelectAllClick : null}
              selectedAll={selectedAll}
              setSelectedAll={setSelectedAll}
            />
            {loading ? (
              <LoadingTableBody
                pagination={pagination}
                dataHeaders={dataHeaders}
                showInfoButton={showInfoButton}
                actions={actions}
              />
            ) : (
              <EnhacedTableBody
                visibleRows={visibleRows}
                dataHeaders={dataHeaders}
                selected={selected}
                cantSelectRow={cantSelectRow}
                handleClickRow={handleClickRow}
                actions={actions}
                finalSumRow={finalSumRow}
                handleClick={handleClick}
                anchorEls={anchorEls}
                openMenus={openMenus}
                completeTable={completeTable}
                pagination={pagination}
                tableSize={tableSize}
                openTooltipIndex={openTooltipIndex}
                setOpenTooltipIndex={setOpenTooltipIndex}
                classes={classes}
                firstCellStyle={firstCellStyle}
                data={data}
                setOpenMenus={setOpenMenus}
                setAnchorEls={setAnchorEls}
                setSelected={setSelected}
                updateValues={updateValues}
                showInfoButton={showInfoButton}
                onInfoClick={onInfoClick}
              />
            )}
          </Table>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: headerBackgroundColor,
            }}
          >
            <div style={{ paddingLeft: '10px', display: 'flex', gap: '8px' }}>
              {exportToFile && (
                <DataExporter
                  buttonColor="transparent"
                  data={data}
                  fileType={exportToFile}
                  headers={exportHeaders ?? dataHeaders}
                />
              )}
              {exportComplete && exportToFile && (
                <DataExporter
                  buttonColor="transparent"
                  data={data}
                  fileType={exportToFile}
                  headers={dataHeaders}
                  searchData={searchData}
                  complete
                  setLoading={setLoading}
                />
              )}
            </div>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 100, 250]}
              component="div"
              count={pagination.total}
              rowsPerPage={pagination.pageSize}
              page={pagination.page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:" // This sets the label to Spanish
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              ActionsComponent={
                showFirstLastPageControls ? TablePaginationActions : undefined
              }
            />
          </div>
        </TableContainer>
      </div>
    </div>
  );
};

export default TableWithPagination;
